<template>
  <VueFinalModal
    v-model="isOpen"
    content-class="flex justify-center items-center w-full h-full overflow-y-auto box-border bg-black/50"
    @closed="() => cancel()"
  >
    <div v-if="isOpen" class="box-border max-h-screen h-full flex items-center w-full overflow-auto py-10 px-4">
      <pe-card
        ref="modalWindow"
        :style="{ width }"
        class="relative mx-auto max-w-full"
        :no-background="noBackground"
      >
        <template v-if="hasHeader" #header>
          <slot name="header"><div class="my-0 py-4 text-xl" v-html="header"></div></slot>
        </template>
        <button
          class="absolute p-0 right-6 top-3 inline-flex h-8 w-8 cursor-pointer items-center justify-center border-0 bg-transparent text-neutral-400 transition-all hover:text-neutral-500"
          @click="() => cancel()"
        >
          <svg class="scale-150" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="currentColor" d="m6.4 18.308l-.708-.708l5.6-5.6l-5.6-5.6l.708-.708l5.6 5.6l5.6-5.6l.708.708l-5.6 5.6l5.6 5.6l-.708.708l-5.6-5.6z"/>
          </svg>
        </button>

        <slot></slot>

        <template v-if="hasFooter" #footer>
          <slot name="footer" :cancel="cancel" :confirm="onConfirm">
            <div class="py-4 text-right text-xl font-normal uppercase space-x-2">
              <UiButton
                v-if="!hideCancelButton"
                type="danger"
                outline
                class="px-6 py-3 text-sm uppercase"
                :disabled="disableCancel"
                tabindex="2"
                @click="() => cancel()"
              >
                {{ cancelButtonText || $t('button.cancel') }}
              </UiButton>
              <UiButton
                id="button-confirm"
                class="px-6 py-3 text-sm uppercase"
                :disabled="disableConfirm"
                @click="() => onConfirm()"
                tabindex="1"
                type="primary"
              >
                {{ confirmButtonText || $t('button.confirm') }}
              </UiButton>
            </div>
          </slot>
        </template>
      </pe-card>
    </div>
  </VueFinalModal>
</template>

<script lang="ts">
export default {
  name: 'PeModal',
  inheritAttrs: false
}
</script>

<script setup lang="ts">
import { computed, ref, useSlots, watch, nextTick } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import { onKeyUp } from '@vueuse/core'

import UiButton from '@/components/ui/button.vue'


const props = defineProps<{
  modelValue?: any
  header?: string
  footer?: string
  width?: string
  hideControls?: boolean
  disableConfirm?: boolean
  hideCancelButton?: boolean
  disableCancel?: boolean
  cancelButtonText?: string
  confirmButtonText?: string
  trapFocus?: boolean
  closeOnEsc?: boolean
  confirmOnEnter?: boolean
  noBackground?: boolean
  beforeClose?: any
  beforeComplete?: any
  loading?: boolean
  beforeOpen?: () => void | boolean | Promise<void | boolean>
  beforeCancel?: () => void | boolean | Promise<void | boolean>
  beforeConfirm?: () => void | boolean | Promise<void | boolean>
}>()

const slots = useSlots()
const emit = defineEmits(['onConfirm', 'onCancel', 'update:modelValue'])
const modalWindow = ref()

const isOpen = ref(false)
const isLoading = computed(() => props.loading || false)

const open = async () => {
  if (props.beforeOpen) {
    const result = await props.beforeOpen()
    if (result === false) return
  }

  isOpen.value = true

  let ready = new Promise (resolve => {
    nextTick(() => {
      resolve(true)
    })
  })

  return await ready
}

const cancel = async () => {
  if (typeof props.beforeCancel !== 'undefined' && await props.beforeCancel() === false) return
  isOpen.value = false
  emit('update:modelValue', false)
}

// const confirm = async () => {
//   if (typeof props.beforeConfirm !== 'undefined' && await props.beforeConfirm() === false) return
//   isOpen.value = false
// }

watch(
  () => props.modelValue,
  (value) => {
    if (value) open()
    else cancel()
  }
)

const hasHeader = computed(() => !!slots.header || !!props.header)
const hasFooter = computed(() => {
  if (props.hideControls) return false
  return true
})

const onCancel = () => {
  if (typeof props.beforeClose !== 'undefined' && !props.beforeClose()) return false
  emit('onCancel')
  emit('update:modelValue', false)
}

const onConfirm = async () => {
  if (typeof props.beforeComplete !== 'undefined' && !(await props.beforeComplete())) return false
  emit('onConfirm')
  emit('update:modelValue', false)
}

onKeyUp('Escape', () => {
  if (props.closeOnEsc) onCancel()
})

onKeyUp('Enter', () => props.confirmOnEnter && onConfirm())

defineExpose({
  isLoading,
  isOpen,
  open,
  cancel,
  onCancel,
  onConfirm
})
</script>
